import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  TextField,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Link,
} from '@mui/material';
import { Close as CloseIcon, Autorenew as AutorenewIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MyButton, MyNotification } from '../index';
import FileUploadField from '../FormField/FileUploadField';
import { useAuth } from '../../hooks';
import { hasuraClient } from '../../utils/graphqlClient';
import { INSERT_CONTRACT_RENEWAL_MUTATION } from '../../graphql/mutations/contractRenewals';

// FileUploadField'in beklediği field şekli — required değil, sadece label
// göstermek için label key sağlıyoruz.
const PROTOCOL_PDF_FIELD = {
  name: 'protocol_pdf',
  label: 'contracts.renewal.protocolPdf',
  type: 'fileupload',
};

// Sözleşmeyi orijinal kaydına dokunmadan yenilemek için yeni bir
// contract_renewals satırı oluşturur. next_renewal_date hesaplaması artık
// MAX(renewal_end_date) üzerinden yapılır.
const ContractRenewalModal = ({
  open = false,
  onClose,
  contract = null,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [description, setDescription] = useState('');
  const [newMonthlyAmount, setNewMonthlyAmount] = useState('');
  const [protocolPdf, setProtocolPdf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (open) {
      const today = new Date().toISOString().slice(0, 10);
      setStartDate(today);
      setEndDate('');
      setDescription('');
      setNewMonthlyAmount('');
      setProtocolPdf(null);
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    setError('');
    if (!startDate || !endDate) {
      setError(t('modals.form.validation.required'));
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setError(t('contracts.renewal.errors.dateRange', 'Bitiş tarihi başlangıçtan önce olamaz'));
      return;
    }
    if (!contract?.id) {
      setError(t('contracts.renewal.errors.noContract', 'Sözleşme bilgisi bulunamadı'));
      return;
    }

    try {
      setLoading(true);
      const companyId = contract.company_id || user?.companyId;
      // new_monthly_amount opsiyonel: kullanıcı fiyat değişikliği yoksa boş
      // bırakır, motor bu yenileme için fiyat değiştirmez. Girilirse ay-bazlı
      // hesap motoru renewal_start_date'ten itibaren bu birimi kullanır.
      const parsedNewMonthly = newMonthlyAmount === '' || newMonthlyAmount == null
        ? null
        : Number(newMonthlyAmount);
      if (parsedNewMonthly != null && !Number.isFinite(parsedNewMonthly)) {
        setError(t('contracts.renewal.errors.invalidAmount', 'Yeni aylık tutar geçersiz'));
        setLoading(false);
        return;
      }
      await hasuraClient.mutate(INSERT_CONTRACT_RENEWAL_MUTATION, {
        object: {
          contract_id: parseInt(contract.id),
          company_id: parseInt(companyId),
          renewal_start_date: startDate,
          renewal_end_date: endDate,
          description: description || null,
          new_monthly_amount: parsedNewMonthly,
          // FileUploadField yüklemede {key, size, ...} objesi döndürür; null
          // veya undefined ise alanlar null kalır.
          protocol_pdf_key: protocolPdf?.key || null,
          protocol_pdf_size: protocolPdf?.size || null,
          created_by_user_id: user?.id ? parseInt(user.id) : null,
        },
      });
      setNotification({
        open: true,
        message: t('contracts.renewal.success', 'Sözleşme başarıyla yenilendi'),
        severity: 'success',
      });
      if (onSuccess) onSuccess();
      onClose?.();
    } catch (e) {
      setError(e?.message || t('errors.generic', 'Bir hata oluştu'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          onClose?.();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutorenewIcon color="primary" />
              <Typography variant="h6">{t('contracts.renewal.modalTitle', 'Sözleşme Yenile')}</Typography>
            </Box>
            <IconButton onClick={onClose} disabled={loading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {/* Mevcut durum özeti: kullanıcıya hangi tarihin baz alındığı net
                görünsün — "neden kırmızı/turuncu" sorusunun cevabı. */}
            {contract && contract.renewal_status && contract.renewal_status !== 'no_renewal' && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor:
                    contract.renewal_status === 'overdue'
                      ? 'rgba(244, 67, 54, 0.08)'
                      : contract.renewal_status === 'due_soon'
                        ? 'rgba(255, 152, 0, 0.08)'
                        : 'rgba(76, 175, 80, 0.08)',
                  border: '1px solid',
                  borderColor:
                    contract.renewal_status === 'overdue'
                      ? 'error.light'
                      : contract.renewal_status === 'due_soon'
                        ? 'warning.light'
                        : 'success.light',
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  {contract.last_renewal_end_date
                    ? t('contracts.renewal.basedOnLast', 'Hesap: son yenileme bitiş tarihine göre')
                    : t('contracts.renewal.basedOnSigning', 'Hesap: imza tarihi + yenileme periyodu (henüz yenileme yok)')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>{t('contracts.renewal.nextDate', 'Sonraki yenileme')}:</strong>{' '}
                  <span style={{ fontFamily: 'monospace' }}>{contract.next_renewal_date || '-'}</span>
                  {' · '}
                  {contract.days_to_renewal != null && (
                    contract.days_to_renewal < 0
                      ? <span style={{ color: '#d32f2f' }}>{Math.abs(contract.days_to_renewal)} gün geçti (kırmızı)</span>
                      : contract.days_to_renewal <= 30
                        ? <span style={{ color: '#ed6c02' }}>{contract.days_to_renewal} gün kaldı (turuncu)</span>
                        : <span style={{ color: '#2e7d32' }}>{contract.days_to_renewal} gün kaldı</span>
                  )}
                </Typography>
              </Box>
            )}
            <TextField
              label={t('contracts.renewal.startDate', 'Yenileme Başlangıç Tarihi')}
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
              disabled={loading}
            />
            <TextField
              label={t('contracts.renewal.endDate', 'Yenileme Bitiş Tarihi')}
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
              disabled={loading}
            />
            <TextField
              label={t('contracts.renewal.newMonthlyAmount', 'Yeni Aylık Tutar (₺)')}
              type="number"
              value={newMonthlyAmount}
              onChange={(e) => setNewMonthlyAmount(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              disabled={loading}
              helperText={t(
                'contracts.renewal.newMonthlyAmountHelp',
                'Boş bırakılırsa eski aylık birim devam eder. Girilirse yenileme başlangıç tarihinden itibaren ciro hesabı bu tutarı kullanır.',
              )}
            />
            <TextField
              label={t('contracts.renewal.description', 'Açıklama')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
              disabled={loading}
            />
            <FileUploadField
              field={PROTOCOL_PDF_FIELD}
              value={protocolPdf}
              onChange={(_name, val) => setProtocolPdf(val)}
              disabled={loading}
            />

            {/* Geçmiş yenilemeler — kullanıcı modal'ı tekrar açtığında
                ne kadar süreyle yenilendiğini görür. Yeni yenileme yine
                listeye eklenir, eskisini override etmez. */}
            {Array.isArray(contract?.renewals) && contract.renewals.length > 0 && (
              <Box>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('contracts.renewal.history', 'Geçmiş Yenilemeler')}
                  <Chip
                    onClick={() => {}}
                    label={contract.renewals.length}
                    size="small"
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                  />
                </Typography>
                <List dense disablePadding sx={{ maxHeight: 180, overflowY: 'auto' }}>
                  {contract.renewals.map((r) => {
                    const pdfUrl = r.protocol_pdf_key
                      ? `https://ai-thinks-hr-docs.s3.eu-north-1.amazonaws.com/${r.protocol_pdf_key}`
                      : null;
                    return (
                      <ListItem key={r.id} sx={{ px: 0, py: 0.5 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ fontFamily: 'monospace' }}>
                                {r.renewal_start_date} → {r.renewal_end_date}
                              </span>
                              {r.new_monthly_amount != null && (
                                <Chip
                                  size="small"
                                  label={`${Number(r.new_monthly_amount).toLocaleString('tr-TR')} ₺/ay`}
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                              {pdfUrl && (
                                <Link href={pdfUrl} target="_blank" rel="noopener" sx={{ fontSize: '0.75rem' }}>
                                  {t('contracts.renewal.protocolPdf', 'Protokol PDF')}
                                </Link>
                              )}
                            </Box>
                          }
                          secondary={r.description || null}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <MyButton variant="outlined" onClick={onClose} disabled={loading}>
            {t('modals.delete.cancel', 'İptal')}
          </MyButton>
          <MyButton variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? t('modals.form.saving', 'Kaydediliyor...') : t('contracts.renewal.submit', 'Yenile')}
          </MyButton>
        </DialogActions>
      </Dialog>
      <MyNotification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification({ ...notification, open: false })}
      />
    </>
  );
};

export default ContractRenewalModal;
